import { Component, OnInit, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, HostListener } from '@angular/core';
import { DisplayAction } from 'src/app/model/map-actions/display-actions';
import { SLLayer, LayerAccessLevel } from 'src/app/model/sl-layer';
import { UUID } from 'angular2-uuid';
import { ModalMessagerService } from 'src/app/modal-messager/modal-messager.service';
import { LayerAttributeViewComponent } from '../layer-attribute-view/layer-attribute-view.component';
import { AttributesService, AttTargetSource } from 'src/app/model/api-services/attributes.service';
import { AttributeInfo, LayerAttributesArray } from 'src/app/types/@types/sl-v-types';
import { AdddAttributeDialogService } from '../dialogs/add-attribute-dialog/addd-attribute-dialog.service';

export type AttributeNode = {
  id: string;
  label: string;
  attribute: AttributeInfo;
}

@Component({
  selector: 'app-layer-attributes-view',
  templateUrl: './layer-attributes-view.component.html',
  styleUrls: ['./layer-attributes-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class LayerAttributesViewComponent implements AfterViewInit {
  public name = 'Layer Attributes';
  public layer: SLLayer;
  public dataSource: AttTargetSource | '';
  public fitHeight = false;
  public collapsed = true;
  public isOpen = true;
  public isClosed = false;
  public ready = false;
  public isEditable = false;
  public serverMenuOpen = false;
  public localMenuOpen = false;

  private _ready: boolean = false;
  private _filterText: string = '';
  public set filterText(text: string) {
    this._filterText = text;
    this.UpdateFilter();
    this.Refresh();
  }

  public get filterText(): string {
    return this._filterText;
  }
  @ViewChild('treeContainer', { static: true }) treeContainer: ElementRef;

  public nodes: Array<AttributeNode> = [];
  public unfilteredNodes: Array<AttributeNode> = [];

  constructor(private domNode: ElementRef, private atts: AttributesService,
    private cdr: ChangeDetectorRef,
    private messager: ModalMessagerService, private attAdder: AdddAttributeDialogService) { }

  ngAfterViewInit(): void {
    this.dataSource = 'self';
    this._ready = true;
    this.Refresh();
  }
  @HostListener('document:click', ['$event'])
  HandleDocumentClick(event: MouseEvent) {
    if (!this.domNode.nativeElement.contains(event.target as Node)) {
      this.CloseMenus();
    }
  }
  ToggleServerMenu(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();
    this.serverMenuOpen = !this.serverMenuOpen;
    if (this.serverMenuOpen) {
      this.localMenuOpen = false;
    }
    this.Refresh();
  }
  ToggleLocalMenu(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();
    this.localMenuOpen = !this.localMenuOpen;
    if (this.localMenuOpen) {
      this.serverMenuOpen = false;
    }
    this.Refresh();
  }
  CloseMenus() {
    if (!this.serverMenuOpen && !this.localMenuOpen) {
      return;
    }
    this.serverMenuOpen = false;
    this.localMenuOpen = false;
    this.Refresh();
  }

  AttemptAdd() {
    this.attAdder.Show((response) => {
      if (response.status === 'error') {
        this.messager.Notify('There was a problem adding the requested attribute');
      } else {
        if (!!response.attName) {
          if (!!response.attInfo) {
            this.layer.layerAttributes.push(response.attInfo as AttributeInfo);
          }
        }
        this.Download();
        this.messager.Notify('Attribute added');
      }
    }, this.layer);
  }
  Back() {
    DisplayAction.SetPrimaryView('layer');
  }
  SetLayer(layer: SLLayer) {
    this.layer = layer;
    this.name = 'Attributes for ' + layer.name;
    this.isEditable = this.layer.access === LayerAccessLevel.Edit;
    this.nodes.splice(0, this.nodes.length);
    this.unfilteredNodes.splice(0, this.unfilteredNodes.length);
    for (const attribute of this.layer.layerAttributes) {
      if (attribute.hasOwnProperty('id') === false) {
        attribute.id = UUID.UUID();
      }
      this.unfilteredNodes.push({
        id: attribute.id,
        label: (attribute.display === '') ? attribute.name : attribute.display,
        attribute: (attribute)
      });
    }
    this.Filter();
    this.Refresh();
  }
  Upload() {
    const title = 'Attribute Upload';
    this.messager.Progress('Uploading attribute changes', false, .5, title);
    if (this.dataSource === '') return;
    this.CloseMenus();
    this.atts.SaveAttributes(this.layer, this.GetAttributes(), this.dataSource).then((response) => {
      if (response.status === 'error') {
        this.messager.CancelProgress(title);
        this.messager.Notify(response.problem,true,'Problem saving changes');
      } else {
        this.messager.Progress('Attribute upload complete.', true, 1, title);
        // this.messager.Notify('Attribute info has been saved', false, title);
      }
    });
  }
  GetAttributes(): LayerAttributesArray {
    const atts: LayerAttributesArray = [];
    for (const node of this.nodes) {
      if (Array.isArray(node.attribute.casting)) {
        const entry = node.attribute.casting.entry ? node.attribute.casting.entry : null;
        const display = node.attribute.casting.display ? node.attribute.casting.display : null;
        node.attribute.casting = {};
        if (!!entry) {
          node.attribute.casting.entry = entry;
        }
        if (!!display) {
          node.attribute.casting.display = display;
        }
      }
      atts.push(node.attribute);
    }
    return atts;
  }
  Download() {
    if (this.dataSource === '') return;
    this.CloseMenus();
    this.atts.GetAttributes(this.layer, this.dataSource).then((results) => {
      this.layer.layerAttributes = results.attributes;
      this.SetLayer(this.layer);
    });
  }
  ClearAndInherit() {
    this.CloseMenus();
    this.messager.Confirm(
      'This will clear any attribute settings for this layer in this map and will inherit attributes from the layer defaults. Press confirm to continue, or cancel to abort this request.',
      () => {
        this.atts.ClearAttributes(this.layer, 'self').then(() => {
          this.Download();
        })
      },
      null,
      'Clear & Inherit?');

  }
  RequestImport() {
    this.CloseMenus();
    this.messager.RequestImport({
      acceptedExtentions: ['.slatts'],
      readType: 'text',
    }, 'Select an attributes file to import:',
      (fileInfo: any) => {
        if (!!fileInfo.contents) {
          const atts = JSON.parse(fileInfo.contents);
          this.layer.layerAttributes = atts;
          this.SetLayer(this.layer);
          this.messager.Notify('The attributes you loaded have been imported into this viewer. To commit these changes, upload the attributes to the layer or layer defaults to keep these changes',
            false, 'Attributes Loaded');
        }
      }, () => { })
  }
  RequestExport() {
    this.CloseMenus();
    this.messager.RequestExport({
      extension: '.slatts',
      contents: JSON.stringify(this.layer.layerAttributes),
      mimeType: 'application/json',
      defaultFileName: this.layer.name + '-attributes'
    }, 'Export attributes to the following file:',
      () => { }, () => { }, 'Attribute Export'
    )
  }
  HandleViewChange() {
    this.Refresh();
  }
  HandleToggle() {

  }
  ShowAttribute(attribute: any) {
    const view: LayerAttributeViewComponent = DisplayAction.SetPrimaryView('layer-attribute-view') as LayerAttributeViewComponent;
    view.ShowAttribute(this.layer, attribute);
    this.Refresh();
  }
  public UpdateHeight(primaryHeight: number, panelMax: number) {
    primaryHeight = primaryHeight;
    this.fitHeight = this.isOpen;
    this.collapsed = this.isClosed;
    this.domNode.nativeElement.style.height = panelMax + 'px';
    this.Refresh();
  }

  public Filter(filterValue?: string) {
    if (!filterValue) {
      filterValue = '';
    }
    this.nodes = this.unfilteredNodes.filter((item: any, index: number) => {
      item = item;
      if (filterValue === '') {
        return true;
      }
      const itemName = this.layer.layerAttributes[index].name.toLowerCase().trim();
      const itemDisplayName = this.layer.layerAttributes[index].display.toLowerCase();
      if (itemName.indexOf(filterValue) >= 0) {
        return true;
      }
      if (itemDisplayName.indexOf(filterValue) >= 0) {
        return true;
      }
      return false;
    })
  }
  ClearFilter() {
    this.filterText = '';
    this.Refresh();
  }
  UpdateFilter() {
    this.Filter(this.filterText);
  }
  Refresh() {
    if (this._ready) {
      this.cdr.detectChanges();
    }
  }

}
